import React, { useRef, useEffect, useState } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { db } from './firebaseConfig';
import '../Css/OrderForm_css.css';
import Footer from './Footer';
import Header from './Header';
import ReviewsSection from './ReviewsSection';
import OrderNotes from './OrderNotes';
import { v4 as uuidv4 } from 'uuid';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Chip,
  Divider
} from '@mui/material';
import { keyframes, styled } from '@mui/system';

// Images
import metalImg from "../Imgs/order1.jpeg";       // existing
import paperImg from "../Imgs/order2.jpeg";   // add or update path

// Styled
const SectionContainer = styled(Box)({
  padding: '40px 24px',
  background: 'linear-gradient(180deg, #faf7ff 0%, #ffffff 60%)',
  textAlign: 'center',
});

const ProductShell = styled(Box)({
  maxWidth: 980,
  margin: '0 auto',
});

const FancyHeader = styled(Typography)({
  fontWeight: 800,
  letterSpacing: 0.3,
  color: '#2a2058',
});

const Hero = styled('div')({
  width: '50%',
  maxWidth: 980,
  margin: '20px auto 6px',
  borderRadius: 16,
  overflow: 'hidden',
  position: 'relative',
  boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
});

const HeroImg = styled('img')({
  width: '100%',
  height: 400,
  objectFit: 'cover',
  display: 'block',
});

const HeroBadge = styled('div')({
  position: 'absolute',
  left: 16,
  bottom: 16,
  background: 'rgba(0,0,0,0.45)',
  color: '#fff',
  padding: '8px 12px',
  borderRadius: 12,
  fontWeight: 700,
  backdropFilter: 'blur(2px)',
});

const OrderPanel = styled(Box)({
  maxWidth: 900,
  margin: '28px auto 40px',
  padding: '24px',
  backgroundColor: '#fff',
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  scrollMarginTop: '100px',
});

const pulseGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(89,62,161,0.0), 0 0 0 0 rgba(89,62,161,0.0); }
  25%  { box-shadow: 0 0 0 4px rgba(89,62,161,0.20), 0 12px 24px rgba(0,0,0,0.10); }
  50%  { box-shadow: 0 0 0 8px rgba(89,62,161,0.28), 0 12px 24px rgba(0,0,0,0.12); }
  75%  { box-shadow: 0 0 0 4px rgba(89,62,161,0.20), 0 8px 20px rgba(0,0,0,0.10); }
  100% { box-shadow: 0 0 0 0 rgba(89,62,161,0.0), 0 8px 24px rgba(0,0,0,0.08); }
`;

const GlowPanel = styled(OrderPanel)(({ active }) => ({
  animation: active ? `${pulseGlow} 1.6s ease-in-out 1` : 'none',
}));

const UploadPreview = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  marginTop: 10,
});

const OrderButton = styled(Button)({
  marginTop: 15,
  backgroundColor: '#593EA1',
  color: '#fff',
  padding: '12px 20px',
  borderRadius: 12,
  fontWeight: 700,
  '&:hover': { backgroundColor: '#452F82' },
});

// Catalog
const METAL_LABEL = 'Classic 5×5 cm Metallic Magnets';
const PAPER_LABEL = 'Magnetic Photo Paper (9×7 cm)';

const metalPackages = [
  { id: 'm-3',  name: '3 Magnets',            price: 7,  magnets: 3,  description: '5×5 cm' },
  { id: 'm-6',  name: '6 Magnets (+1 Free)',  price: 14, magnets: 6,  description: '5×5 cm' },
  { id: 'm-9',  name: '9 Magnets (+2 Free)',  price: 18, magnets: 9,  description: '5×5 cm' },
  { id: 'm-12', name: '12 Magnets (+2 Free)', price: 22, magnets: 12, description: '5×5 cm' },
  { id: 'm-18', name: '18 Magnets',           price: 26, magnets: 18, description: '5×5 cm' },
];

const paperPackages = [
  { id: 'p-3',  name: '3 Paper Magnets',  price: 6,  magnets: 3,  description: '9×7 cm' },
  { id: 'p-6',  name: '6 Paper Magnets',  price: 12, magnets: 6,  description: '9×7 cm' },
  { id: 'p-9',  name: '9 Paper Magnets',  price: 16, magnets: 9,  description: '9×7 cm' },
  { id: 'p-12', name: '12 Paper Magnets', price: 20, magnets: 12, description: '9×7 cm' },
  { id: 'p-18', name: '18 Paper Magnets', price: 24, magnets: 18, description: '9×7 cm' },
];

const productTabs = [
  { key: 'metal', icon: '🧲', label: METAL_LABEL, data: metalPackages, accent: '#5b42f3', hero: metalImg },
  { key: 'paper', icon: '🖼️', label: PAPER_LABEL, data: paperPackages, accent: '#0ea5e9', hero: paperImg },
];

const OrderForm = () => {
  const [activeProduct, setActiveProduct] = useState('metal');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [orderDetails, setOrderDetails] = useState({ name: '', email: '', phone: '', address: '' });
  const [imageFiles, setImageFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [glow, setGlow] = useState(false);

  const orderFormRef = useRef(null);
  const refId = useRef(uuidv4());

  // Pre-fill email if signed in
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setOrderDetails(prev => ({ ...prev, email: user.email || '' }));
      }
    });
    return () => unsubscribe();
  }, []);

  const currentCatalog = productTabs.find(p => p.key === activeProduct);
  const packages = currentCatalog?.data || [];
  const heroImg = currentCatalog?.hero || metalImg;

  // No scrolling on tab change; reset selection/images
  const handleTabChange = (_e, value) => {
    setActiveProduct(value);
    setSelectedPackage(null);
    setImageFiles([]);
    setImageUrls([]);
    setError('');
  };

  // YES: scroll + glow on package select
  const handlePackageSelect = (event) => {
    const selectedId = event.target.value;
    const pkg = packages.find((p) => p.id === selectedId) || null;
    setSelectedPackage(pkg);
    setImageFiles([]);
    setImageUrls([]);
    setError('');
    if (orderFormRef.current) {
      orderFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setGlow(false);
    requestAnimationFrame(() => {
      setGlow(true);
      setTimeout(() => setGlow(false), 1600);
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    if (!selectedPackage) {
      setError('Please choose a package first.');
      return;
    }
    const files = e.target.files || [];
    if (files.length !== selectedPackage.magnets) {
      setError(`Please upload exactly ${selectedPackage.magnets} images.`);
      return;
    }
    setImageFiles(Array.from(files));
    setError('');
  };

  const deleteExistingImages = async () => {
    const storage = getStorage();
    const folderRef = ref(storage, `images/${refId.current}`);
    try {
      const listResult = await listAll(folderRef);
      const deletePromises = listResult.items.map((itemRef) => deleteObject(itemRef));
      await Promise.all(deletePromises);
    } catch (err) {
      console.error('Error deleting existing images:', err);
    }
  };

  const uploadImagesToStorage = async () => {
    setUploading(true);
    await deleteExistingImages();
    const storage = getStorage();

    const uploadPromises = imageFiles.map((file) => {
      const storageRef = ref(storage, `images/${refId.current}/${file.name}`);
      return uploadBytes(storageRef, file).then(async (snapshot) => {
        const downloadUrl = await getDownloadURL(snapshot.ref);
        return downloadUrl;
      });
    });

    try {
      const urls = await Promise.all(uploadPromises);
      if (urls.length !== (selectedPackage?.magnets || 0)) {
        setError(`Failed to upload ${selectedPackage?.magnets} images. Please try again.`);
        return null;
      }
      setImageUrls(urls);
      setError('');
      return urls;
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload images. Please try again.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const calculateTotalPrice = () => (selectedPackage ? selectedPackage.price : 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPackage) {
      setError('Please choose a package first.');
      return;
    }
    if (imageFiles.length !== selectedPackage.magnets) {
      setError(`Please upload exactly ${selectedPackage.magnets} images.`);
      return;
    }

    const uploadedImageUrls = await uploadImagesToStorage();
    if (!uploadedImageUrls) return;

    const productTypeLabel = activeProduct === 'metal' ? 'Classic Metallic' : 'Magnetic Photo Paper';
    const packageLabel = `${productTypeLabel} — ${selectedPackage.name} (${selectedPackage.description})`;

    const orderData = {
      Address: orderDetails.address,
      Date: Timestamp.now(),
      Email: orderDetails.email,
      Name: orderDetails.name,
      Package: packageLabel,  // same field, descriptive
      Phone: orderDetails.phone,
      Price: calculateTotalPrice(),
      client_feedback: null,
      imageUrls: uploadedImageUrls,
      order_delivered: false,
      order_done: false,
      refId: refId.current,
    };

    try {
      const docRef = await addDoc(collection(db, 'orders'), orderData);
      console.log('Document written with ID: ', docRef.id);
      setSelectedPackage(null);
      setOrderDetails({ name: '', email: '', phone: '', address: '' });
      setImageFiles([]);
      setImageUrls([]);
      setSnackbarMessage('Order submitted successfully!');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error adding document: ', err);
      setSnackbarMessage('Failed to submit order. Please try again.');
      setSnackbarOpen(true);
    }
  };

  return (
    <div className="product-page">
      <OrderNotes />

      {/* Product type + single hero image + single Select */}
      <SectionContainer>
        <ProductShell>
          <FancyHeader variant="h4" sx={{ mb: 1 }}>
            Select Your Product
          </FancyHeader>
          <Typography variant="subtitle1" sx={{ color: '#5a5a5a', mb: 2 }}>
            Choose between our classic metallic magnets or our new magnetic photo paper.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tabs
              value={activeProduct}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 48 },
                '& .Mui-selected': { color: (productTabs.find(p=>p.key===activeProduct)?.accent) || '#593EA1' },
                '& .MuiTabs-indicator': { height: 4, borderRadius: 2, background: (productTabs.find(p=>p.key===activeProduct)?.accent) || '#593EA1' },
              }}
            >
              {productTabs.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: 20 }}>{t.icon}</span>
                      <span>{t.label}</span>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* Single dynamic product image */}
          <Hero>
            <HeroImg src={heroImg} alt={activeProduct === 'metal' ? METAL_LABEL : PAPER_LABEL} loading="lazy" />
            <HeroBadge>{activeProduct === 'metal' ? 'Classic Metallic • 5×5 cm' : 'Magnetic Photo Paper • 9×7 cm'}</HeroBadge>
          </Hero>

          {/* Single selection input (packages) */}
          <Box sx={{ mt: 2, maxWidth: 560, mx: 'auto', textAlign: 'left' }}>
            <FormControl fullWidth>
              <InputLabel id="package-select-label">Choose a Package</InputLabel>
              <Select
                labelId="package-select-label"
                id="package-select"
                value={selectedPackage ? selectedPackage.id : ''}
                onChange={handlePackageSelect}
                label="Choose a Package"
              >
                {packages.map((pkg) => (
                  <MenuItem
                    key={pkg.id}
                    value={pkg.id}
                    style={{ whiteSpace: 'normal', height: 'auto', paddingTop: 8, paddingBottom: 8 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {pkg.name} — ${pkg.price}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Size: {pkg.description} • Upload {pkg.magnets} image{pkg.magnets > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* quick “benefit” chips */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {activeProduct === 'metal' ? (
                <>
                  <Chip label="Crisp metallic finish" variant="outlined" />
                  <Chip label="Durable & thin" variant="outlined" />
                  <Chip label="Perfect for gifts" variant="outlined" />
                </>
              ) : (
                <>
                  <Chip label="Larger 9×7 cm print" variant="outlined" />
                  <Chip label="Light & flexible" variant="outlined" />
                  <Chip label="Great for collages" variant="outlined" />
                </>
              )}
            </Box>
          </Box>
        </ProductShell>
      </SectionContainer>

      {/* Order Form (scroll + glow when a package is chosen) */}
      <GlowPanel ref={orderFormRef} active={glow ? 1 : 0}>
        {selectedPackage ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip label={activeProduct === 'metal' ? 'Classic Metallic' : 'Photo Paper'} color="primary" variant="outlined" />
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#593EA1' }}>
                {selectedPackage.name} — {activeProduct === 'metal' ? '5×5 cm' : '9×7 cm'}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Please upload exactly {selectedPackage.magnets} image{selectedPackage.magnets > 1 ? 's' : ''}.
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Name" name="name" value={orderDetails.name} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email" name="email" type="email" value={orderDetails.email} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone" name="phone" type="tel" value={orderDetails.phone} onChange={handleInputChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Address" name="address" value={orderDetails.address} onChange={handleInputChange} required />
                </Grid>

                <Grid item xs={12}>
                  <input
                    type="file"
                    multiple
                    onChange={handleImageUpload}
                    required
                    style={{ display: 'none' }}
                    id="upload-images"
                    accept="image/*"
                  />
                  <label htmlFor="upload-images">
                    <Button variant="contained" component="span" fullWidth disabled={!selectedPackage}>
                      Upload Images ({selectedPackage.magnets} required)
                    </Button>
                  </label>

                  {uploading && <CircularProgress sx={{ mt: 2 }} />}

                  <UploadPreview>
                    {imageFiles.map((file, index) => (
                      <img
                        key={index}
                        src={URL.createObjectURL(file)}
                        alt={`Selected ${index + 1}`}
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}
                      />
                    ))}
                  </UploadPreview>

                  {error && (
                    <Typography color="error" sx={{ mt: 1 }}>
                      {error}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Total Price: ${selectedPackage ? selectedPackage.price : 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Delivery: $4 (All Over Lebanon).
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <OrderButton
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={uploading || !orderDetails.name || !orderDetails.email || !orderDetails.phone || !orderDetails.address}
                  >
                    {uploading ? 'Uploading...' : 'Submit Order'}
                  </OrderButton>
                </Grid>
              </Grid>
            </form>
          </>
        ) : (
          <Typography sx={{ textAlign: 'center', color: '#6b6b6b' }}>
            Select a package above to enter your details and upload photos.
          </Typography>
        )}
      </GlowPanel>

      <ReviewsSection />
      <Footer />

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={error ? 'error' : 'success'}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OrderForm;
